import {
  ApplicantTaskActivity,
  ApplicantTaskProgress,
  ApplicantTaskStatus,
  DocumentationAgency,
  DocumentationStep,
  DocumentationTask,
  ResourceBookingSchema,
} from "../data/types";

export const TASK_STATUS_OPTIONS: { id: ApplicantTaskStatus; label: string; dot: string }[] = [
  { id: "not_started", label: "Not Started", dot: "bg-[#9CA3AF]" },
  { id: "submitted", label: "Submitted", dot: "bg-[#3B82F6]" },
  { id: "in_review", label: "In Review", dot: "bg-[#F59E0B]" },
  { id: "fix_required", label: "Fix Required", dot: "bg-[#EF4444]" },
  { id: "completed", label: "Completed", dot: "bg-[#22C55E]" },
];

export const ARLINGTON_AGENCIES: DocumentationAgency[] = [
  {
    name: "Arlington County Environmental Health",
    address: "2100 Clarendon Blvd, Arlington, VA 22201",
    email: "environmentalhealth@arlingtonva.us",
    phone: "(703) 228-7400",
  },
  {
    name: "Arlington County Inspection Services",
    address: "2100 Clarendon Blvd, Arlington, VA 22201",
    email: "inspections@arlingtonva.us",
    phone: "(703) 228-3800",
  },
  {
    name: "Arlington County Zoning Division",
    address: "2100 Clarendon Blvd, Arlington, VA 22201",
    email: "zoning@arlingtonva.us",
    phone: "(703) 228-3883",
  },
  {
    name: "Virginia Department of Agriculture and Consumer Services (VDACS)",
    address: "102 Governor St, Richmond, VA 23219",
    email: "foodsafety@vdacs.virginia.gov",
    phone: "(804) 786-3520",
  },
];

const createId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `doc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const emptyAgency = (): DocumentationAgency => ({
  name: "",
  address: "",
  email: "",
  phone: "",
});

/** Create a distinct applicant task that describes the parent step. */
export const createDefaultTaskFromStep = (
  step: Pick<DocumentationStep, "title" | "description" | "agency">,
  order = 0
): DocumentationTask => ({
  id: createId(),
  title: step.title?.trim() || "Complete this step",
  description:
    step.description?.trim() ||
    "Complete the applicant action needed for this documentation checkpoint.",
  agency: step.agency ? { ...step.agency } : emptyAgency(),
  order,
});

export const createEmptyTask = (order = 0): DocumentationTask => ({
  id: createId(),
  title: "",
  description: "",
  agency: emptyAgency(),
  order,
});

export const createEmptyStep = (order = 0): DocumentationStep => {
  const step: DocumentationStep = {
    id: createId(),
    title: "",
    description: "",
    agency: emptyAgency(),
    tasks: [],
    order,
  };
  return ensureStepHasDefaultTask(step);
};

/**
 * Every step must have at least one task. If none exist, add a distinct task
 * that describes the step — do not treat the step record itself as the task.
 */
export const ensureStepHasDefaultTask = (step: DocumentationStep): DocumentationStep => {
  if (step.tasks && step.tasks.length > 0) {
    return {
      ...step,
      tasks: step.tasks.map((task, index) => ({ ...task, order: task.order ?? index })),
    };
  }
  return {
    ...step,
    tasks: [createDefaultTaskFromStep(step, 0)],
  };
};

export const ensureStepsHaveDefaultTasks = (steps: DocumentationStep[]): DocumentationStep[] =>
  steps.map((step, index) =>
    ensureStepHasDefaultTask({
      ...step,
      order: step.order ?? index,
    })
  );

export const collectAgencies = (steps: DocumentationStep[] = []): DocumentationAgency[] => {
  const byName = new Map<string, DocumentationAgency>();
  ARLINGTON_AGENCIES.forEach((agency) => {
    if (agency.name) byName.set(agency.name, agency);
  });
  steps.forEach((step) => {
    if (step.agency?.name) byName.set(step.agency.name, step.agency);
    (step.tasks || []).forEach((task) => {
      if (task.agency?.name) byName.set(task.agency.name, task.agency);
    });
  });
  return Array.from(byName.values());
};

const eh = ARLINGTON_AGENCIES[0];
const isd = ARLINGTON_AGENCIES[1];
const zoning = ARLINGTON_AGENCIES[2];
const vdacs = ARLINGTON_AGENCIES[3];

/**
 * Optional Arlington starter checklist from the Aug 5 Environmental Health
 * discussion and Aug 26 Inspection Services code-consultation prep.
 * HomeCook collects records and routes to the County — it does not approve.
 */
export const getArlingtonStarterSteps = (): DocumentationStep[] =>
  ensureStepsHaveDefaultTasks([
    {
      id: createId(),
      title: "Confirm licensing lane",
      description:
        "Classify the business as food service (Arlington Environmental Health / VDH), manufacturing or packaged food (VDACS), mobile, or mixed before applying.",
      agency: eh,
      order: 0,
      tasks: [
        {
          id: createId(),
          title: "Submit business category, type, and intended products",
          description:
            "Tell the partner which products you will make and whether you serve food, manufacture/package, or operate a mobile unit so they can route you to the right agency. HomeCook does not determine eligibility.",
          agency: eh,
          order: 0,
        },
      ],
    },
    {
      id: createId(),
      title: "Host kitchen / commissary / base of operation",
      description:
        "Capture the host license, latest inspection, and — for food trucks — a Base of Operation Agreement. A shared production kitchen is not automatically a mobile unit’s base.",
      agency: eh,
      order: 1,
      tasks: [
        {
          id: createId(),
          title: "Upload host agreement and current health license",
          description:
            "Upload the signed host/commissary agreement, the host’s current health license, and the latest inspection report.",
          agency: eh,
          order: 0,
        },
      ],
    },
    {
      id: createId(),
      title: "Operator food-establishment permit",
      description:
        "Each independent business may need its own permit. An existing host approval does not automatically cover third-party users.",
      agency: eh,
      order: 2,
      tasks: [
        {
          id: createId(),
          title: "Apply or attach permit / plan-review status",
          description:
            "Upload your food-establishment permit or plan-review status from Arlington Environmental Health. Platform document status is not regulatory approval.",
          agency: eh,
          order: 0,
        },
      ],
    },
    {
      id: createId(),
      title: "Certified Food Protection Manager",
      description:
        "Arlington requires recognized CFPM credentials. Confirm who must hold the credential for the host, each business, and each operating shift.",
      agency: eh,
      order: 3,
      tasks: [
        {
          id: createId(),
          title: "Upload CFPM credential and expiration",
          description:
            "Upload a recognized Certified Food Protection Manager certificate and note the expiration date.",
          agency: eh,
          order: 0,
        },
      ],
    },
    {
      id: createId(),
      title: "Zoning for intended use",
      description:
        "The intended use must be allowed by the Zoning Ordinance before a Certificate of Occupancy. Inspection Services does not decide zoning.",
      agency: zoning,
      order: 4,
      tasks: [
        {
          id: createId(),
          title: "Confirm or request Zoning review",
          description:
            "Confirm the host property’s allowed use for food production or request a Zoning review before treating the site as pilot-ready.",
          agency: zoning,
          order: 0,
        },
      ],
    },
    {
      id: createId(),
      title: "Certificate of Occupancy / change of use",
      description:
        "A CO confirms Building Code and County-law compliance. Shared use may require a host CO, a tenant CO, or both — Inspection Services confirms the rule for each site.",
      agency: isd,
      order: 5,
      tasks: [
        {
          id: createId(),
          title: "Upload current CO and approved use / occupancy",
          description:
            "Upload the current Certificate of Occupancy and approved use/occupancy classification. Do not treat an existing kitchen as ready just because equipment is present.",
          agency: isd,
          order: 0,
        },
      ],
    },
    {
      id: createId(),
      title: "Building systems / occupant-load screen",
      description:
        "Screen hood/exhaust, fire suppression, sprinklers/alarm, plumbing/restrooms, egress, accessibility, and overlapping occupancy. Alterations can trigger a building permit even without a layout change.",
      agency: isd,
      order: 6,
      tasks: [
        {
          id: createId(),
          title: "Upload floor plan, occupant-load sign, and equipment list",
          description:
            "Upload the approved floor plan, occupant-load documentation, and cooking equipment list. Flag any planned alterations so Inspection Services can say whether a permit is required.",
          agency: isd,
          order: 0,
        },
        {
          id: createId(),
          title: "Flag manufacturing or packaged-food activity for VDACS",
          description:
            "If you manufacture, process, or pack food, note it here so the partner can route you to VDACS instead of treating it as a Building Code question.",
          agency: vdacs,
          order: 1,
        },
      ],
    },
  ]);

export const flattenStepTasks = (steps: DocumentationStep[] = []): Array<DocumentationTask & { step_id: string; step_title?: string }> =>
  steps.flatMap((step) =>
    (step.tasks || []).map((task) => ({
      ...task,
      step_id: step.id,
      step_title: step.title,
    }))
  );

export const findProgress = (
  checklist: ApplicantTaskProgress[] | undefined,
  stepId: string,
  taskId: string
): ApplicantTaskProgress | undefined =>
  (checklist || []).find((item) => item.step_id === stepId && item.task_id === taskId);

export type ApplicantProgressSummary = {
  total: number
  completed: number
  byStatus: Record<ApplicantTaskStatus, number>
  newComments: number
};

export const emptyProgressSummary = (): ApplicantProgressSummary => ({
  total: 0,
  completed: 0,
  byStatus: {
    not_started: 0,
    submitted: 0,
    in_review: 0,
    fix_required: 0,
    completed: 0,
  },
  newComments: 0,
});

export const summarizeApplicantProgress = (
  steps: DocumentationStep[] = [],
  checklist: ApplicantTaskProgress[] = []
): ApplicantProgressSummary => {
  const summary = emptyProgressSummary();
  const tasks = flattenStepTasks(steps);
  summary.total = tasks.length;
  tasks.forEach((task) => {
    const progress = findProgress(checklist, task.step_id, task.id);
    const status = (progress?.status || "not_started") as ApplicantTaskStatus;
    summary.byStatus[status] += 1;
    if (status === "completed") summary.completed += 1;
    summary.newComments += progress?.activity?.length || 0;
  });
  return summary;
};

export const isPendingApplicant = (booking: ResourceBookingSchema): boolean =>
  (booking.status || "").toLowerCase() === "pending";

export const isApprovedApplicant = (booking: ResourceBookingSchema): boolean => {
  const status = (booking.status || "").toLowerCase();
  return status === "approved" || status === "in_progress" || status === "completed";
};

export const upsertProgressStatus = (
  checklist: ApplicantTaskProgress[] = [],
  stepId: string,
  taskId: string,
  status: ApplicantTaskStatus
): ApplicantTaskProgress[] => {
  const next = checklist.map((item) => ({ ...item, activity: item.activity ? [...item.activity] : [] }));
  const index = next.findIndex((item) => item.step_id === stepId && item.task_id === taskId);
  if (index >= 0) {
    next[index] = { ...next[index], status };
    return next;
  }
  next.push({ step_id: stepId, task_id: taskId, status, activity: [] });
  return next;
};

export const appendTaskActivity = (
  checklist: ApplicantTaskProgress[] = [],
  stepId: string,
  taskId: string,
  activity: ApplicantTaskActivity
): ApplicantTaskProgress[] => {
  const next = checklist.map((item) => ({ ...item, activity: item.activity ? [...item.activity] : [] }));
  const index = next.findIndex((item) => item.step_id === stepId && item.task_id === taskId);
  if (index >= 0) {
    next[index] = {
      ...next[index],
      activity: [...(next[index].activity || []), activity],
    };
    return next;
  }
  next.push({
    step_id: stepId,
    task_id: taskId,
    status: "not_started",
    activity: [activity],
  });
  return next;
};

export const formatRelativeSubmitted = (created?: string): string => {
  if (!created) return "N/A";
  const then = new Date(created).getTime();
  if (Number.isNaN(then)) return "N/A";
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export const formatConsultation = (startTime?: string): string => {
  if (!startTime) return "N/A";
  const date = new Date(startTime);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleString("en-US", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const hasAgencyContact = (agency?: DocumentationAgency): boolean =>
  Boolean(agency?.name || agency?.email || agency?.address || agency?.phone);
